﻿import { getErrorMessage } from '@/lib/functions';
import { formatGroupedAmount, parseGroupedAmount } from '@/lib/amountFormat';
import { useTranslation } from 'react-i18next';
﻿import { Ionicons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useLocalSearchParams, useNavigation, useRouter } from 'expo-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Button } from '@/components/ui/Button';
import { DatePickerField } from '@/components/ui/DatePickerField';
import { Input } from '@/components/ui/Input';
import { WizardLayout } from '@/components/ui/WizardLayout';
import { DisclaimerNote } from '@/components/DisclaimerNote';
import { ConfirmModal } from '@/components/ConfirmModal';
import { CURRENCIES } from '@/lib/currencies';
import { contentMaxWidth, fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/contexts/ProfileContext';
import type { AmountUnit } from '@/lib/database.types';
import { checkDailyAgreementLimit, checkPairAgreementLimit, createAgreement, findProfileByEmail, sendAgreement } from '@/lib/agreements';
import { supabase } from '@/lib/supabase';
import { searchProfiles, suggestProfiles, type SearchProfileResult } from '@/lib/profiles';
import type { Profile } from '@/lib/database.types';
import { validateEmail } from '@/lib/validation';
import { fillTemplate, listAgreementTemplates, type AgreementTemplate } from '@/lib/agreementTemplates';
import { formatAmount } from '@/lib/format';

const COACH_KEY = 'new_agreement_coach_v1';

function getCoachSteps(t: (key: string) => string) {
  return [
    { icon: 'create-outline' as const, title: t('agreements.coach1_title'), text: t('agreements.coach1_text') },
    { icon: 'calendar-outline' as const, title: t('agreements.coach2_title'), text: t('agreements.coach2_text') },
    { icon: 'person-outline' as const, title: t('agreements.coach3_title'), text: t('agreements.coach3_text') },
    { icon: 'cash-outline' as const, title: t('agreements.coach4_title'), text: t('agreements.coach4_text') },
    { icon: 'document-text-outline' as const, title: t('agreements.coach5_title'), text: t('agreements.coach5_text') },
  ];
}

type Errors = { title?: string; email?: string; amount?: string };

function parseAmount(raw: string): number | null | undefined {
  if (!raw.trim()) return null;
  const value = parseGroupedAmount(raw);
  if (value == null || !Number.isFinite(value) || value < 0) return undefined;
  return value;
}

export default function NewAgreementScreen() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { profile } = useProfile();
  const router = useRouter();
  const navigation = useNavigation();
  const insets = useSafeAreaInsets();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const COACH_STEPS = useMemo(() => getCoachSteps(t), [t]);
  /** Όταν true, το επόμενο navigation event (back/tab/etc.) δεν διακόπτεται
   * από τον οδηγό "αποθήκευση προχείρου" — το βάζουμε πριν από κάθε
   * προγραμματισμένη έξοδο που κάνουμε εμείς οι ίδιοι μετά από επιβεβαίωση. */
  const bypassLeaveGuardRef = useRef(false);


  const EXPIRY_LABELS: { key: string; label: string }[] = [
    { key: 'Ημερομηνία λήξης',      label: t('agreements.expiry_due') },
    { key: 'Ημερομηνία Παράδοσης',  label: t('agreements.expiry_delivery') },
    { key: 'Ημερομηνία εξόφλησης',  label: t('agreements.expiryLabelPayment') },
  ];

  const { counterpartyId, counterpartyName } = useLocalSearchParams<{
    counterpartyId?: string;
    counterpartyName?: string;
  }>();
  const prefilled = !!counterpartyId;

  const [title, setTitle] = useState('');
  const [agreementType, setAgreementType] = useState<'one_off' | 'ongoing' | 'fixed_term'>('one_off');
  const [expiryDate, setExpiryDate] = useState<Date | null>(null);
  const [expiryLabel, setExpiryLabel] = useState('Ημερομηνία λήξης');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [email, setEmail] = useState('');
  const [unregisteredName, setUnregisteredName] = useState('');
  const [amount, setAmount] = useState('');
  const [amountUnit, setAmountUnit] = useState<AmountUnit>('eur');
  /** Ίδιο pattern με το new-multi.tsx: κρατάει ό,τι πληκτρολογεί ο χρήστης αυτολεξεί
   * (π.χ. "35." πριν το "2") ώστε να μη κόβεται η τελεία σε κάθε render. */
  const [percentEditingKey, setPercentEditingKey] = useState<'creator' | 'counterparty' | null>(null);
  const [percentEditingText, setPercentEditingText] = useState('');
  const [currency, setCurrency] = useState('EUR');
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
  const [description, setDescription] = useState('');
  const [templates, setTemplates] = useState<AgreementTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

  useEffect(() => {
    void listAgreementTemplates().then(setTemplates).catch(() => setTemplates([]));
  }, []);
  const [errors, setErrors] = useState<Errors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [pairBlock, setPairBlock] = useState<{ availableAt: string; counterpartyId: string; availableAtIso: string } | null>(null);
  // Θεματικό (ίδιο look με το υπόλοιπο app) prompt "αποθήκευση ως πρόχειρο ή
  // απόρριψη" όταν ο χρήστης προσπαθεί να φύγει με μη αποθηκευμένα στοιχεία —
  // αντικαθιστά το παλιό, άσχημο native Alert.alert. Κρατάει τη συνάρτηση
  // που πρέπει να τρέξει αν επιλέξει "Απόρριψη" (διαφέρει ανάλογα με το πώς
  // έγινε η προσπάθεια εξόδου).
  const [leavePromptDiscard, setLeavePromptDiscard] = useState<(() => void) | null>(null);
  const [partyModeChosen, setPartyModeChosen] = useState(prefilled);

  const [tooltip, setTooltip] = useState<number | null>(null);

  // ── Bottom sheet coach ──
  const [coachStep, setCoachStep] = useState<number | null>(null);

  useEffect(() => {
    void AsyncStorage.getItem(COACH_KEY).then((v) => {
      if (!v) setTimeout(() => setCoachStep(0), 400);
    });
  }, []);

  function nextCoach() {
    const next = (coachStep ?? 0) + 1;
    if (next >= COACH_STEPS.length) {
      setCoachStep(null);
      void AsyncStorage.setItem(COACH_KEY, '1');
    } else {
      setCoachStep(next);
    }
  }

  function skipCoach() {
    setCoachStep(null);
    void AsyncStorage.setItem(COACH_KEY, '1');
  }

  type UserSuggestion = { id: string; full_name: string | null };
  const [nameSuggestions, setNameSuggestions] = useState<SearchProfileResult[]>([]);
  const [emailSuggestion, setEmailSuggestion] = useState<UserSuggestion | null>(null);
  const [selectedUser, setSelectedUser] = useState<UserSuggestion | null>(null);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isEmailMode = email.includes('@');

  useEffect(() => {
    setNameSuggestions([]);
    setEmailSuggestion(null);
    if (selectedUser) return;
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const trimmed = email.trim();
    if (!trimmed) return;

    if (isEmailMode) {
      if (trimmed.length < 5) return;
      searchTimer.current = setTimeout(async () => {
        try {
          const found = await findProfileByEmail(trimmed);
          if (found && found.id !== user?.id) setEmailSuggestion(found);
        } catch { /* αγνόησε */ }
      }, 500);
    } else {
      if (trimmed.length < 2) return;
      searchTimer.current = setTimeout(async () => {
        try {
          const results = await searchProfiles(trimmed, user?.id);
          if (results.length > 0) {
            setNameSuggestions(results.slice(0, 5));
          } else {
            const fuzzy = await suggestProfiles(trimmed, user?.id);
            setNameSuggestions(fuzzy.slice(0, 5));
          }
        } catch { /* αγνόησε */ }
      }, 350);
    }
    return () => { if (searchTimer.current) clearTimeout(searchTimer.current); };
  }, [email]); // eslint-disable-line react-hooks/exhaustive-deps

  function validateStep1(): boolean {
    const next: Errors = {};
    if (!title.trim()) next.title = t('agreements.errorTitleRequired');
    if (!prefilled && !selectedUser) {
      if (isEmailMode) next.email = validateEmail(email) ?? undefined;
      else if (email.trim().length < 2) next.email = t('agreements.errorCounterpartyRequired');
    }
    if (agreementType === 'fixed_term') {
      if (!startDate) next.title = next.title ?? t('agreements.errorStartDateRequired');
      if (!endDate) next.title = next.title ?? t('agreements.errorEndDateRequired');
      if (startDate && endDate && endDate <= startDate) next.title = t('agreements.errorEndAfterStart');
    }
    if (next.title || next.email) { setErrors(next); return false; }
    setErrors({});
    return true;
  }

  function validateStep2(): boolean {
    const next: Errors = {};
    const parsedAmount = parseAmount(amount);
    if (parsedAmount === undefined) next.amount = t('agreements.errorInvalidAmount');
    if (next.amount) { setErrors(next); return false; }
    setErrors({});
    return true;
  }

  async function onSubmit() {
    setFormError(null);
    const parsedAmount = parseAmount(amount);
    if (parsedAmount === undefined) { setFormError(t('agreements.errorInvalidAmount')); return; }
    setErrors({});

    setLoading(true);
    try {
      // Freeze / suspension check
      const { data: freezeStatusRaw } = await supabase.rpc('get_my_freeze_status');
      const freezeStatus = freezeStatusRaw as { suspended?: boolean; frozen?: boolean } | null;
      if (freezeStatus?.suspended) {
        setFormError(t('agreements.errorSuspended'));
        return;
      }
      if (freezeStatus?.frozen) {
        setFormError(t('agreements.errorFrozen'));
        return;
      }

      const dailyCheck = await checkDailyAgreementLimit(user!.id);
      if (!dailyCheck.allowed) {
        setFormError(t('agreements.errorDailyLimit', { limit: dailyCheck.limit }));
        return;
      }

      let resolvedCounterpartyId: string | null = counterpartyId ?? null;
      let inviteEmail: string | null = null;

      if (!prefilled) {
        if (selectedUser) {
          resolvedCounterpartyId = selectedUser.id;
        } else {
          const counterparty = await findProfileByEmail(email);
          if (counterparty) {
            if (counterparty.id === user?.id) {
              setErrors({ email: t('agreements.errorSelfAgreement') });
              return;
            }
            resolvedCounterpartyId = counterparty.id;
          } else {
            resolvedCounterpartyId = null;
            inviteEmail = email.trim().toLowerCase();
          }
        }
      } else if (resolvedCounterpartyId === user?.id) {
        setFormError(t('agreements.errorSelfAgreement'));
        return;
      }

      if (resolvedCounterpartyId) {
        const pairCheck = await checkPairAgreementLimit(user!.id, resolvedCounterpartyId);
        if (!pairCheck.allowed && pairCheck.available_at) {
          const availableDate = new Date(pairCheck.available_at);
          const formatted = availableDate.toLocaleString('el-GR', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
          setPairBlock({ availableAt: formatted, counterpartyId: resolvedCounterpartyId, availableAtIso: pairCheck.available_at });
          return;
        }
      }

      const agreement = await createAgreement({
        creatorId: user!.id,
        counterpartyId: resolvedCounterpartyId,
        counterpartyEmail: inviteEmail,
        counterpartyName: inviteEmail ? (unregisteredName.trim() || null) : null,
        title: title.trim(),
        description: description.trim() || null,
        amount: parsedAmount ?? null,
        amountUnit,
        currency: amountUnit === 'percent' ? 'EUR' : currency,
        type: agreementType,
        expiresAt: agreementType === 'one_off' && expiryDate ? (() => { const d = new Date(expiryDate); d.setHours(23, 59, 59, 0); return d.toISOString(); })() : null,
        expiresAtLabel: agreementType === 'one_off' && expiryDate ? expiryLabel : null,
        startDate: agreementType === 'fixed_term' && startDate ? startDate.toISOString().slice(0, 10) : null,
        endDate: agreementType === 'fixed_term' && endDate ? endDate.toISOString().slice(0, 10) : null,
        includeProfessionalTerms: false,
      });

      // Ο χρήστης ήδη επισκόπησε και επιβεβαίωσε στο βήμα 3 — μη ζητάς
      // ξανά επιβεβαίωση αποστολής στην οθόνη της συμφωνίας (2η ίδια
      // επισκόπηση). Στείλε κατευθείαν αντί να μείνει σε 'draft'.
      await sendAgreement(agreement.id);

      // Η συμφωνία στάλθηκε — δεν υπάρχει τίποτα «μη αποθηκευμένο» πλέον.
      // Χωρίς αυτό, το beforeRemove guard έβλεπε ακόμα γεμάτα τα πεδία της
      // φόρμας και ρωτούσε «να αποθηκευτεί ως πρόχειρο;» αμέσως μετά την
      // επιτυχή αποστολή.
      bypassLeaveGuardRef.current = true;
      router.replace({ pathname: '/(app)/agreements/[id]', params: { id: agreement.id } });
    } catch (e) {
      setFormError(getErrorMessage(e, t('agreements.errorCreateFailed')));
    } finally {
      setLoading(false);
    }
  }

  /** Αποθηκεύει ό,τι έχει συμπληρωθεί ως πρόχειρο (χωρίς αποστολή στον αντισυμβαλλόμενο). */
  async function saveAsDraftAndExit() {
    setLoading(true);
    try {
      const parsedAmount = parseAmount(amount);
      let resolvedCounterpartyId: string | null = counterpartyId ?? null;
      if (!prefilled && selectedUser) resolvedCounterpartyId = selectedUser.id;
      await createAgreement({
        creatorId: user!.id,
        counterpartyId: resolvedCounterpartyId,
        title: title.trim() || t('agreements.untitled'),
        description: description.trim() || null,
        amount: parsedAmount ?? null,
        amountUnit,
        currency: amountUnit === 'percent' ? 'EUR' : currency,
        type: agreementType,
        expiresAt: agreementType === 'one_off' && expiryDate ? (() => { const d = new Date(expiryDate); d.setHours(23, 59, 59, 0); return d.toISOString(); })() : null,
        expiresAtLabel: agreementType === 'one_off' && expiryDate ? expiryLabel : null,
        startDate: agreementType === 'fixed_term' && startDate ? startDate.toISOString().slice(0, 10) : null,
        endDate: agreementType === 'fixed_term' && endDate ? endDate.toISOString().slice(0, 10) : null,
        includeProfessionalTerms: false,
      });
      bypassLeaveGuardRef.current = true;
      router.replace('/(app)/agreements');
    } catch (e) {
      setFormError(getErrorMessage(e, t('agreements.errorSaveDraftFailed')));
      setLoading(false);
    }
  }

  function hasUnsavedData(): boolean {
    return !prefilled && (title.trim().length > 0 || email.trim().length > 0 || !!selectedUser || amount.trim().length > 0);
  }

  function confirmClose() {
    if (!hasUnsavedData()) { bypassLeaveGuardRef.current = true; router.back(); return; }
    setLeavePromptDiscard(() => () => { bypassLeaveGuardRef.current = true; router.back(); });
  }

  // Πιάνει ΚΑΘΕ τρόπο εξόδου από την οθόνη (πλήκτρο πίσω Android, swipe-back,
  // tap σε tab του κάτω μενού) — όχι μόνο το ρητό X του WizardLayout.
  useEffect(() => {
    const unsubscribe = navigation.addListener('beforeRemove', (e) => {
      if (bypassLeaveGuardRef.current || !hasUnsavedData()) return;
      e.preventDefault();
      setLeavePromptDiscard(() => () => {
        bypassLeaveGuardRef.current = true;
        navigation.dispatch(e.data.action);
      });
    });
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigation, title, email, selectedUser, amount, prefilled]);

  async function saveCooldownDraft() {
    if (!pairBlock) return;
    setLoading(true);
    try {
      const parsedAmount = parseAmount(amount);
      await createAgreement({
        creatorId: user!.id,
        counterpartyId: pairBlock.counterpartyId,
        title: title.trim(),
        description: description.trim() || null,
        amount: parsedAmount ?? null,
        currency: amountUnit === 'percent' ? 'EUR' : currency,
        amountUnit,
        type: agreementType,
        expiresAt: agreementType === 'one_off' && expiryDate ? (() => { const d = new Date(expiryDate); d.setHours(23, 59, 59, 0); return d.toISOString(); })() : null,
        expiresAtLabel: agreementType === 'one_off' && expiryDate ? expiryLabel : null,
        startDate: agreementType === 'fixed_term' && startDate ? startDate.toISOString().slice(0, 10) : null,
        endDate: agreementType === 'fixed_term' && endDate ? endDate.toISOString().slice(0, 10) : null,
        sendAvailableAt: pairBlock.availableAtIso,
        includeProfessionalTerms: false,
      });
      // Μόλις αποθηκεύτηκε ως πρόχειρο — μη ρωτήσεις ξανά για αποθήκευση.
      bypassLeaveGuardRef.current = true;
      router.replace('/(app)/agreements');
    } catch (e) {
      setFormError(getErrorMessage(e, t('agreements.errorSaveFailed')));
      setPairBlock(null);
    } finally {
      setLoading(false);
    }
  }

  const counterpartyDisplay = prefilled
    ? (counterpartyName?.trim() || t('agreements.selectedUserFallback'))
    : selectedUser
    ? (selectedUser.full_name || email)
    : email.trim() || '—';

  const creatorPercent = parseAmount(amount) || 0;
  const counterpartyPercent = Math.round((100 - creatorPercent) * 100) / 100;

  function percentDisplayValue(key: 'creator' | 'counterparty', numericValue: number): string {
    return percentEditingKey === key ? percentEditingText : String(numericValue);
  }

  function stopEditingPercent() {
    setPercentEditingKey(null);
  }

  function onChangeCreatorPercent(text: string) {
    setPercentEditingKey('creator');
    setPercentEditingText(text);
    const v = Number(text.replace(',', '.'));
    if (!Number.isFinite(v)) return;
    setAmount(String(Math.max(0, Math.min(100, v))));
  }

  function onChangeCounterpartyPercent(text: string) {
    setPercentEditingKey('counterparty');
    setPercentEditingText(text);
    const v = Number(text.replace(',', '.'));
    if (!Number.isFinite(v)) return;
    setAmount(String(Math.round((100 - Math.max(0, Math.min(100, v))) * 100) / 100));
  }

  function applyTemplate(tpl: AgreementTemplate | null) {
    setSelectedTemplateId(tpl?.id ?? null);
    if (!tpl) return;
    const parsedAmount = parseAmount(amount);
    const amountText = parsedAmount != null
      ? (amountUnit === 'percent' ? `${parsedAmount}%` : formatAmount(parsedAmount, currency))
      : '';
    const professionalName = profile?.full_name?.trim() || '';
    const businessName = profile?.show_business_name && profile?.business_name?.trim()
      ? profile.business_name.trim()
      : professionalName;
    const counterpartyName = counterpartyDisplay !== '—' ? counterpartyDisplay : '';
    // "...ανέγνωσε τους όρους της επιχείρησης Χ" έχει νόημα μόνο όταν ο
    // δημιουργός είναι πράγματι επαγγελματίας με ενεργό προφίλ — αλλιώς
    // αναφέρεται σε ανύπαρκτη "επιχείρηση".
    const businessTermsClause = profile?.portfolio_enabled
      ? t('agreements.templateBusinessTermsClause', { name: counterpartyName, business: businessName })
      : '';
    setDescription(fillTemplate(tpl.template_text, {
      counterparty_name: counterpartyName,
      amount: amountText,
      professional_name: professionalName,
      business_name: businessName,
      business_terms_clause: businessTermsClause,
    }).trim());
  }

  if (!partyModeChosen) {
    return (
      <View style={[styles.flex, { paddingTop: insets.top + spacing.xl, paddingHorizontal: spacing.lg }]}>
        <WizardLayout step={1} totalSteps={1} title={t('agreements.newAgreementTitle')} subtitle={t('agreements.partyModeSubtitle')} onClose={() => router.back()} />
        <View style={{ gap: spacing.md, marginTop: spacing.xl }}>
          <Pressable style={styles.modeCard} onPress={() => setPartyModeChosen(true)} accessibilityRole="button">
            <Ionicons name="person-outline" size={28} color={colors.primary} />
            <Text style={styles.modeCardTitle}>{t('agreements.twoPartyTitle')}</Text>
            <Text style={styles.modeCardSubtitle}>{t('agreements.twoPartySubtitle')}</Text>
          </Pressable>
          <Pressable style={styles.modeCard} onPress={() => router.push('/(app)/agreements/new-multi')} accessibilityRole="button">
            <Ionicons name="people-outline" size={28} color={colors.primary} />
            <Text style={styles.modeCardTitle}>{t('agreements.multiPartyTitle')}</Text>
            <Text style={styles.modeCardSubtitle}>{t('agreements.multiPartySubtitle')}</Text>
          </Pressable>
        </View>
      </View>
    );
  }

  return (
    <KeyboardAvoidingView
      style={styles.flex}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
    >
      <ScrollView
        contentContainerStyle={[
          styles.content,
          { paddingTop: insets.top + spacing.lg, paddingBottom: insets.bottom + spacing.lg },
        ]}
        keyboardShouldPersistTaps="handled"
      >
        {/* ── Wizard Header ── */}
        <WizardLayout
          step={wizardStep}
          totalSteps={3}
          title={wizardStep === 1 ? t('agreements.wizardStep1Title') : wizardStep === 2 ? t('agreements.wizardStep2Title') : t('agreements.wizardStep3Title')}
          subtitle={
            wizardStep === 1
              ? t('agreements.wizardStep1Subtitle')
              : wizardStep === 2
              ? t('agreements.wizardStep2Subtitle')
              : t('agreements.wizardStep3Subtitle')
          }
          onBack={wizardStep > 1 ? () => { setWizardStep((s) => s - 1); setErrors({}); } : undefined}
          onClose={confirmClose}
        />

        <View style={styles.form}>

          {/* ══ Βήμα 1 ══ */}
          {wizardStep === 1 && (
            <>
              {/* Τίτλος */}
              <View style={styles.field}>
                <View style={styles.labelRow}>
                  <Text style={styles.fieldLabel}>{t('agreements.field_title')}</Text>
                  <Pressable hitSlop={8} onPress={() => setTooltip(0)}>
                    <Ionicons name="information-circle-outline" size={16} color={colors.textMuted} />
                  </Pressable>
                </View>
                <Input
                  value={title}
                  onChangeText={setTitle}
                  error={errors.title}
                  placeholder={t('agreements.titlePlaceholder')}
                />
              </View>

              {/* Αντισυμβαλλόμενος */}
              <View style={styles.field}>
                <View style={styles.labelRow}>
                  <Text style={styles.fieldLabel}>{t('agreements.field_counterparty')}</Text>
                  <Pressable hitSlop={8} onPress={() => setTooltip(2)}>
                    <Ionicons name="information-circle-outline" size={16} color={colors.textMuted} />
                  </Pressable>
                </View>
                {prefilled ? (
                  <View style={styles.readonly}>
                    <Text style={styles.readonlyText}>{counterpartyName?.trim() || t('agreements.selectedUserFallback')}</Text>
                  </View>
                ) : selectedUser ? (
                  <>
                    <View style={styles.suggestionSelected}>
                      <View style={styles.suggestionSelectedInfo}>
                        <Ionicons name="person-circle-outline" size={22} color={colors.primary} />
                        <Text style={styles.readonlyText}>{selectedUser.full_name || email}</Text>
                      </View>
                      <Pressable hitSlop={8} onPress={() => { setSelectedUser(null); setEmailSuggestion(null); setNameSuggestions([]); setEmail(''); }}>
                        <Ionicons name="close-circle" size={20} color={colors.textMuted} />
                      </Pressable>
                    </View>
                    <Text style={styles.hint}>{t('hint.counterpartyNotification')}</Text>
                  </>
                ) : (
                  <>
                    <View style={[styles.searchInputWrap, !!errors.email && styles.searchInputWrapError]}>
                      <Ionicons name="search" size={15} color={colors.textMuted} style={{ marginRight: 4 }} />
                      <TextInput
                        style={styles.searchInputField}
                        value={email}
                        onChangeText={(v) => { setEmail(v); setSelectedUser(null); }}
                        placeholder={t('agreements.counterpartyPlaceholder')}
                        placeholderTextColor={colors.textMuted}
                        keyboardType={isEmailMode ? 'email-address' : 'default'}
                        autoCapitalize="none"
                        autoCorrect={false}
                      />
                      {email.length > 0 && (
                        <Pressable hitSlop={8} onPress={() => { setEmail(''); setNameSuggestions([]); setEmailSuggestion(null); }}>
                          <Ionicons name="close-circle" size={17} color={colors.textMuted} />
                        </Pressable>
                      )}
                    </View>
                    {!!errors.email && <Text style={styles.formError}>{errors.email}</Text>}
                    {!isEmailMode && nameSuggestions.length > 0 && (
                      <View style={styles.suggestionList}>
                        {nameSuggestions.map((p) => (
                          <Pressable
                            key={p.id}
                            style={({ pressed }) => [styles.suggestion, pressed && { opacity: 0.75 }]}
                            onPress={() => { setSelectedUser({ id: p.id, full_name: p.full_name }); setEmail(''); }}
                          >
                            <Ionicons name="person-circle-outline" size={20} color={colors.primary} />
                            <View style={styles.suggestionInfo}>
                              <Text style={styles.suggestionName}>{p.full_name ?? '—'}</Text>
                              {!!p.specialty && <Text style={styles.suggestionSub}>{p.specialty}</Text>}
                            </View>
                            <Ionicons name="chevron-forward" size={16} color={colors.textMuted} />
                          </Pressable>
                        ))}
                      </View>
                    )}
                    {isEmailMode && emailSuggestion && (
                      <Pressable style={styles.suggestion} onPress={() => { setSelectedUser(emailSuggestion); setEmail(''); }}>
                        <Ionicons name="person-circle-outline" size={20} color={colors.primary} />
                        <View style={styles.suggestionInfo}>
                          <Text style={styles.suggestionName}>{emailSuggestion.full_name || email}</Text>
                          <Text style={styles.suggestionSub}>{t('hint.existsInApp')}</Text>
                        </View>
                        <Ionicons name="chevron-forward" size={16} color={colors.textMuted} />
                      </Pressable>
                    )}
                    <Text style={styles.hint}>
                      {isEmailMode
                        ? t('agreements.counterpartyInviteHint')
                        : t('agreements.counterpartySearchHint')}
                    </Text>
                    {isEmailMode && !emailSuggestion && email.includes('@') && email.length > 4 && (
                      <Input
                        value={unregisteredName}
                        onChangeText={setUnregisteredName}
                        placeholder={t('agreements.unregisteredNamePlaceholder')}
                        autoCapitalize="words"
                      />
                    )}
                  </>
                )}
              </View>

              {/* Τύπος & Διάρκεια συμφωνίας */}
              <View style={styles.field}>
                <View style={styles.labelRow}>
                  <Text style={styles.fieldLabel}>{t('agreements.type_label')}</Text>
                  <Pressable hitSlop={8} onPress={() => setTooltip(1)}>
                    <Ionicons name="information-circle-outline" size={16} color={colors.textMuted} />
                  </Pressable>
                </View>
                <View style={styles.typeCards}>
                  {([
                    { key: 'one_off' as const, label: t('agreements.type_one_off'), icon: 'flash-outline', sub: t('agreements.type_one_off_sub') },
                    { key: 'ongoing' as const, label: t('agreements.type_ongoing'), icon: 'infinite-outline', sub: t('agreements.type_ongoing_sub') },
                    { key: 'fixed_term' as const, label: t('agreements.type_fixed_term'), icon: 'calendar-outline', sub: t('agreements.type_fixed_term_sub') },
                  ] as const).map((opt) => {
                    const sel = agreementType === opt.key;
                    return (
                      <Pressable
                        key={opt.key}
                        onPress={() => setAgreementType(opt.key)}
                        style={[styles.typeCard, sel && styles.typeCardSelected]}
                        accessibilityRole="button"
                      >
                        <Ionicons name={opt.icon} size={20} color={sel ? colors.primary : colors.textMuted} />
                        <Text style={[styles.typeCardLabel, sel && styles.typeCardLabelSelected]}>{opt.label}</Text>
                        <Text style={styles.typeCardSub}>{opt.sub}</Text>
                      </Pressable>
                    );
                  })}
                </View>

                {/* Εφάπαξ: προαιρετική ημερομηνία ορόσημο */}
                {agreementType === 'one_off' && (
                  <>
                    <Text style={[styles.fieldLabel, { marginTop: 4 }]}>{t('agreements.milestoneDateLabel')}</Text>
                    <DatePickerField value={expiryDate} onChange={setExpiryDate} minimumDate={new Date()} />
                    <Text style={styles.hint}>
                      {expiryDate
                        ? t('agreements.milestoneDateHintSet')
                        : t('agreements.milestoneDateHintEmpty')}
                    </Text>
                    {expiryDate && (
                      <View style={styles.labelChips}>
                        {EXPIRY_LABELS.map((opt) => {
                          const sel = expiryLabel === opt.key;
                          return (
                            <Pressable
                              key={opt.key}
                              onPress={() => setExpiryLabel(opt.key)}
                              style={[styles.labelChip, sel && styles.labelChipSelected]}
                              accessibilityRole="button"
                            >
                              <Text style={[styles.labelChipText, sel && styles.labelChipTextSelected]}>{opt.label}</Text>
                            </Pressable>
                          );
                        })}
                      </View>
                    )}
                  </>
                )}

                {/* Από-Έως: έναρξη + λήξη */}
                {agreementType === 'fixed_term' && (
                  <>
                    <Text style={[styles.fieldLabel, { marginTop: 4 }]}>{t('agreements.startDateLabel')}</Text>
                    <DatePickerField value={startDate} onChange={setStartDate} minimumDate={new Date()} />
                    <Text style={[styles.fieldLabel, { marginTop: 4 }]}>{t('agreements.endDateLabel')}</Text>
                    <DatePickerField
                      value={endDate}
                      onChange={setEndDate}
                      minimumDate={startDate ?? new Date()}
                    />
                    {startDate && endDate && (
                      <Text style={styles.hint}>
                        {t('agreements.fixedTermDurationHint', { days: Math.round((endDate.getTime() - startDate.getTime()) / 86400000) })}
                      </Text>
                    )}
                  </>
                )}

                {/* Διαρκής: καμία ημερομηνία */}
                {agreementType === 'ongoing' && (
                  <Text style={styles.hint}>{t('agreements.ongoingHint')}</Text>
                )}
              </View>

              <Button title={t('common.next')} onPress={() => { if (validateStep1()) setWizardStep(2); }} />
            </>
          )}

          {/* ══ Βήμα 2 ══ */}
          {wizardStep === 2 && (
            <>
              {/* Ποσό */}
              <View style={styles.field}>
                <View style={styles.labelRow}>
                  <Text style={styles.fieldLabel}>
                    {amountUnit === 'percent' ? t('agreements.amountPercentLabel') : t('agreements.amountEurLabel')}
                  </Text>
                  <Pressable hitSlop={8} onPress={() => setTooltip(3)}>
                    <Ionicons name="information-circle-outline" size={16} color={colors.textMuted} />
                  </Pressable>
                </View>
                {amountUnit === 'percent' ? (
                  <>
                    <View style={styles.unitToggleRow}>
                      {(['eur', 'percent'] as AmountUnit[]).map((u) => {
                        const sel = amountUnit === u;
                        return (
                          <Pressable
                            key={u}
                            onPress={() => setAmountUnit(u)}
                            style={[styles.unitChip, sel && styles.unitChipSelected]}
                            accessibilityRole="button"
                          >
                            <Text style={[styles.unitChipText, sel && styles.unitChipTextSelected]}>
                              {u === 'eur' ? '€' : '%'}
                            </Text>
                          </Pressable>
                        );
                      })}
                    </View>
                    <View style={styles.shareRow}>
                      <Text style={styles.shareName} numberOfLines={1}>{t('agreements.multi_youCreator')}</Text>
                      <View style={styles.shareInputWrap}>
                        <TextInput
                          style={[styles.shareInput, !!errors.amount && styles.amountRowError]}
                          value={percentDisplayValue('creator', creatorPercent)}
                          onChangeText={onChangeCreatorPercent}
                          onBlur={stopEditingPercent}
                          keyboardType="decimal-pad"
                        />
                        <Text style={styles.sharePercentSign}>%</Text>
                      </View>
                    </View>
                    <View style={styles.shareRow}>
                      <Text style={styles.shareName} numberOfLines={1}>{counterpartyDisplay}</Text>
                      <View style={styles.shareInputWrap}>
                        <TextInput
                          style={[styles.shareInput, !!errors.amount && styles.amountRowError]}
                          value={percentDisplayValue('counterparty', counterpartyPercent)}
                          onChangeText={onChangeCounterpartyPercent}
                          onBlur={stopEditingPercent}
                          keyboardType="decimal-pad"
                        />
                        <Text style={styles.sharePercentSign}>%</Text>
                      </View>
                    </View>
                  </>
                ) : (
                  <View style={[styles.amountRow, !!errors.amount && styles.amountRowError]}>
                    <TextInput
                      style={styles.amountInput}
                      value={amount}
                      onChangeText={(v) => setAmount(formatGroupedAmount(v))}
                      placeholder={t('agreements.amountEurPlaceholder')}
                      placeholderTextColor={colors.textMuted}
                      keyboardType="decimal-pad"
                    />
                    <View style={styles.unitToggle}>
                      {(['eur', 'percent'] as AmountUnit[]).map((u) => {
                        const sel = amountUnit === u;
                        return (
                          <Pressable
                            key={u}
                            onPress={() => setAmountUnit(u)}
                            style={[styles.unitChip, sel && styles.unitChipSelected]}
                            accessibilityRole="button"
                          >
                            <Text style={[styles.unitChipText, sel && styles.unitChipTextSelected]}>
                              {u === 'eur' ? '€' : '%'}
                            </Text>
                          </Pressable>
                        );
                      })}
                    </View>
                  </View>
                )}
                {!!errors.amount && <Text style={styles.formError}>{errors.amount}</Text>}
                {amountUnit !== 'percent' && (
                  <View>
                    {/* Trigger — δείχνει μόνο το επιλεγμένο νόμισμα */}
                    <Pressable
                      style={styles.currencyTrigger}
                      onPress={() => setShowCurrencyDropdown((v) => !v)}
                      accessibilityRole="button"
                    >
                      {(() => { const sel = CURRENCIES.find((c) => c.code === currency)!; return (
                        <>
                          <Text style={styles.currencyTriggerSymbol}>{sel.symbol}</Text>
                          <Text style={styles.currencyTriggerLabel}>{i18n.language.startsWith('en') ? sel.en : sel.el} ({sel.code})</Text>
                          <Ionicons
                            name={showCurrencyDropdown ? 'chevron-up' : 'chevron-down'}
                            size={14}
                            color={colors.textMuted}
                          />
                        </>
                      ); })()}
                    </Pressable>
                    {/* Dropdown */}
                    {showCurrencyDropdown && (
                      <View style={styles.currencyDropdown}>
                        {CURRENCIES.map((c) => {
                          const sel = currency === c.code;
                          return (
                            <Pressable
                              key={c.code}
                              style={[styles.currencyOption, sel && styles.currencyOptionSelected]}
                              onPress={() => { setCurrency(c.code); setShowCurrencyDropdown(false); }}
                              accessibilityRole="button"
                            >
                              <Text style={[styles.currencyOptionSymbol, sel && styles.currencyOptionSymbolSelected]}>{c.symbol}</Text>
                              <Text style={[styles.currencyOptionLabel, sel && styles.currencyOptionLabelSelected]}>
                                {i18n.language.startsWith('en') ? c.en : c.el} <Text style={styles.currencyOptionCode}>{c.code}</Text>
                              </Text>
                              {sel && <Ionicons name="checkmark" size={14} color={colors.primary} style={{ marginLeft: 'auto' }} />}
                            </Pressable>
                          );
                        })}
                      </View>
                    )}
                  </View>
                )}
                <Text style={styles.hint}>
                  {amountUnit === 'percent'
                    ? t('agreements.amountPercentHint')
                    : t('agreements.amountEurHint', { currency })}
                </Text>
              </View>

              {/* Πρότυπα κειμένου δέσμευσης */}
              {templates.length > 0 && (
                <View style={styles.field}>
                  <Text style={styles.fieldLabel}>{t('agreements.templatesLabel')}</Text>
                  <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.templateChipRow}>
                    <Pressable
                      style={[styles.templateChip, selectedTemplateId === null && styles.templateChipActive]}
                      onPress={() => applyTemplate(null)}
                    >
                      <Text style={[styles.templateChipText, selectedTemplateId === null && styles.templateChipTextActive]}>
                        {t('agreements.templateFreeText')}
                      </Text>
                    </Pressable>
                    {templates.map((tpl) => (
                      <Pressable
                        key={tpl.id}
                        style={[styles.templateChip, selectedTemplateId === tpl.id && styles.templateChipActive]}
                        onPress={() => applyTemplate(tpl)}
                      >
                        <Text style={[styles.templateChipText, selectedTemplateId === tpl.id && styles.templateChipTextActive]} numberOfLines={1}>
                          {tpl.name}
                        </Text>
                      </Pressable>
                    ))}
                  </ScrollView>
                </View>
              )}

              {/* Όροι δέσμευσης */}
              <View style={styles.field}>
                <View style={styles.labelRow}>
                  <Text style={styles.fieldLabel}>{t('agreements.field_termsOptional')}</Text>
                  <Pressable hitSlop={8} onPress={() => setTooltip(4)}>
                    <Ionicons name="information-circle-outline" size={16} color={colors.textMuted} />
                  </Pressable>
                </View>
                <Input
                  value={description}
                  onChangeText={setDescription}
                  placeholder={t('agreements.termsPlaceholder')}
                  multiline
                  numberOfLines={7}
                  style={styles.multiline}
                />
                <Text style={styles.hint}>{t('hint.bothPartiesCommit')}</Text>
              </View>

              <Button title={t('common.preview')} onPress={() => { if (validateStep2()) setWizardStep(3); }} />
            </>
          )}

          {/* ══ Βήμα 3 — Επισκόπηση ══ */}
          {wizardStep === 3 && (
            <>
              <View style={styles.reviewCard}>
                <Text style={styles.reviewTitle}>{t('agreements.review_summary_title')}</Text>

                <View style={styles.reviewRow}>
                  <Text style={styles.reviewLabel}>{t('agreements.field_title')}</Text>
                  <Text style={styles.reviewValue}>{title.trim()}</Text>
                </View>
                <View style={styles.reviewRow}>
                  <Text style={styles.reviewLabel}>{t('agreements.field_counterparty')}</Text>
                  <Text style={styles.reviewValue}>{counterpartyDisplay}</Text>
                </View>
                <View style={styles.reviewRow}>
                  <Text style={styles.reviewLabel}>{t('agreements.reviewTypeLabel')}</Text>
                  <Text style={styles.reviewValue}>
                    {agreementType === 'one_off' ? t('agreements.type_one_off') : agreementType === 'ongoing' ? t('agreements.type_ongoing') : t('agreements.type_fixed_term')}
                  </Text>
                </View>
                {agreementType === 'one_off' && (
                  <View style={styles.reviewRow}>
                    <Text style={styles.reviewLabel}>{t('agreements.fields.due_date')}</Text>
                    <Text style={styles.reviewValue}>
                      {expiryDate ? `${expiryLabel}: ${expiryDate.toLocaleDateString('el-GR')}` : t('agreements.reviewOpenDuration')}
                    </Text>
                  </View>
                )}
                {agreementType === 'fixed_term' && startDate && endDate && (
                  <View style={styles.reviewRow}>
                    <Text style={styles.reviewLabel}>{t('agreements.reviewPeriodLabel')}</Text>
                    <Text style={styles.reviewValue}>
                      {startDate.toLocaleDateString('el-GR')} – {endDate.toLocaleDateString('el-GR')}
                    </Text>
                  </View>
                )}
                {amountUnit === 'percent' ? (
                  <>
                    <View style={styles.reviewRow}>
                      <Text style={styles.reviewLabel}>{t('agreements.multi_youCreator')}</Text>
                      <Text style={styles.reviewValue}>{creatorPercent}%</Text>
                    </View>
                    <View style={styles.reviewRow}>
                      <Text style={styles.reviewLabel}>{counterpartyDisplay}</Text>
                      <Text style={styles.reviewValue}>{counterpartyPercent}%</Text>
                    </View>
                  </>
                ) : (
                  <View style={styles.reviewRow}>
                    <Text style={styles.reviewLabel}>{t('agreements.field_amount')}</Text>
                    <Text style={styles.reviewValue}>
                      {amount.trim() ? `${amount} ${CURRENCIES.find((c) => c.code === currency)?.symbol ?? currency}` : '—'}
                    </Text>
                  </View>
                )}
                {!!description.trim() && (
                  <View style={styles.reviewRowFull}>
                    <Text style={styles.reviewLabel}>{t('agreements.field_termsOptional')}</Text>
                    <Text style={styles.reviewValueMuted}>{description.trim()}</Text>
                  </View>
                )}
              </View>

              <Text style={styles.hint}>
                {t('agreements.submitHint')}
              </Text>

              {pairBlock ? (
                <View style={styles.cooldownBox}>
                  <Ionicons name="time-outline" size={22} color="#FBBF24" />
                  <View style={styles.cooldownInfo}>
                    <Text style={styles.cooldownTitle}>{t('agreements.cooldown12Title')}</Text>
                    <Text style={styles.cooldownText}>
                      {t('agreements.cooldown12Body')}{' '}
                      <Text style={styles.cooldownTime}>{pairBlock.availableAt}</Text>
                    </Text>
                    <Button title={t('agreements.action_saveDraft')} icon="bookmark-outline" onPress={saveCooldownDraft} loading={loading} />
                    <Pressable hitSlop={8} onPress={() => setPairBlock(null)}>
                      <Text style={styles.cooldownCancel}>{t('common.cancel')}</Text>
                    </Pressable>
                  </View>
                </View>
              ) : (
                <>
                  {!!formError && <Text style={styles.formError}>{formError}</Text>}
                  <Button title={t('agreements.action_send_agreement')} icon="send-outline" onPress={onSubmit} loading={loading} />
                </>
              )}
              <DisclaimerNote />
            </>
          )}
        </View>
      </ScrollView>

      {/* ── Tooltip modal ── */}
      {tooltip !== null && (
        <Modal visible transparent animationType="fade" onRequestClose={() => setTooltip(null)}>
          <Pressable style={styles.tooltipOverlay} onPress={() => setTooltip(null)}>
            <View style={styles.tooltipCard}>
              <View style={styles.tooltipHeader}>
                <Ionicons name={COACH_STEPS[tooltip].icon} size={20} color={colors.primary} />
                <Text style={styles.tooltipTitle}>{COACH_STEPS[tooltip].title}</Text>
              </View>
              <Text style={styles.tooltipText}>{COACH_STEPS[tooltip].text}</Text>
              <Pressable style={styles.tooltipClose} onPress={() => setTooltip(null)}>
                <Text style={styles.tooltipCloseText}>{t('common.understood')}</Text>
              </Pressable>
            </View>
          </Pressable>
        </Modal>
      )}

      {/* ── Bottom sheet coach ── */}
      {coachStep !== null && (
        <Modal visible transparent animationType="slide" onRequestClose={skipCoach}>
          <View style={StyleSheet.absoluteFill} pointerEvents="box-none">
            <Pressable style={styles.coachOverlay} onPress={() => {}} />
            <View style={[styles.coachSheet, { paddingBottom: insets.bottom + spacing.lg }]}>
              <View style={styles.coachHandle} />
              <View style={styles.coachDots}>
                {COACH_STEPS.map((_, i) => (
                  <View key={i} style={[styles.coachDot, i === coachStep && styles.coachDotActive]} />
                ))}
              </View>
              <View style={styles.coachIconWrap}>
                <Ionicons name={COACH_STEPS[coachStep].icon} size={32} color={colors.primary} />
              </View>
              <Text style={styles.coachTitle}>{COACH_STEPS[coachStep].title}</Text>
              <Text style={styles.coachText}>{COACH_STEPS[coachStep].text}</Text>
              <View style={styles.coachActions}>
                <Pressable onPress={skipCoach} hitSlop={8}>
                  <Text style={styles.coachSkip}>{t('common.skip')}</Text>
                </Pressable>
                <Pressable style={styles.coachNext} onPress={nextCoach}>
                  <Text style={styles.coachNextText}>
                    {coachStep < COACH_STEPS.length - 1 ? t('offersScreens.coachNext') : t('offersScreens.coachStart')}
                  </Text>
                </Pressable>
              </View>
            </View>
          </View>
        </Modal>
      )}

      <ConfirmModal
        visible={!!leavePromptDiscard}
        title={t('agreements.saveDraftPromptTitle')}
        message={t('agreements.saveDraftPromptBody')}
        confirmLabel={t('common.save')}
        cancelLabel={t('common.cancel')}
        loading={loading}
        onConfirm={() => { setLeavePromptDiscard(null); void saveAsDraftAndExit(); }}
        onCancel={() => setLeavePromptDiscard(null)}
        extraAction={{ label: t('agreements.discard'), danger: true, onPress: () => { leavePromptDiscard?.(); setLeavePromptDiscard(null); } }}
      />
    </KeyboardAvoidingView>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    flex: { flex: 1, backgroundColor: colors.background },
    content: {
      // Σε μεγάλες οθόνες η φόρμα μένει σε αναγνώσιμο πλάτος αντί να
      // τεντώνεται· σε κινητό το maxWidth δεν ενεργοποιείται ποτέ.
      width: '100%',
      maxWidth: contentMaxWidth.form,
      alignSelf: 'center',
      flexGrow: 1,
      paddingHorizontal: spacing.lg,
      gap: spacing.lg,
    },
    searchInputWrap: {
      flexDirection: 'row',
      alignItems: 'center',
      height: 52,
      backgroundColor: colors.surface,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radius.md,
      paddingLeft: spacing.md,
      paddingRight: spacing.sm,
    },
    searchInputWrapError: { borderColor: colors.danger },
    searchInputField: { flex: 1, color: colors.text, fontSize: fontSize.md },
    modeCard: {
      backgroundColor: colors.surface,
      borderRadius: radius.lg,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.lg,
      gap: spacing.xs,
    },
    modeCardTitle: { color: colors.text, fontSize: fontSize.lg, fontWeight: '700', marginTop: spacing.xs },
    modeCardSubtitle: { color: colors.textMuted, fontSize: fontSize.sm },
    topBar: { flexDirection: 'row' },
    back: {
      color: colors.primaryLight,
      fontSize: fontSize.md,
      fontWeight: '600',
    },
    header: { gap: spacing.sm },
    title: {
      color: colors.text,
      fontSize: fontSize.xl,
      fontWeight: '700',
    },
    subtitle: {
      color: colors.textMuted,
      fontSize: fontSize.md,
    },
    form: { gap: spacing.md },
    field: { gap: spacing.xs },
    labelRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.xs,
    },
    fieldLabel: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      fontWeight: '500',
      flex: 1,
    },
    hint: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
    },
    templateChipRow: {
      flexDirection: 'row',
      gap: spacing.sm,
      paddingVertical: spacing.xs,
    },
    templateChip: {
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.sm,
      borderRadius: radius.full,
      backgroundColor: colors.surfaceAlt,
      borderWidth: 1,
      borderColor: colors.border,
      maxWidth: 220,
    },
    templateChipActive: {
      backgroundColor: colors.primary,
      borderColor: colors.primary,
    },
    templateChipText: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      fontWeight: '600',
    },
    templateChipTextActive: {
      color: colors.textOnPrimary,
    },
    amountRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
      height: 52,
      backgroundColor: colors.surface,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radius.md,
      paddingLeft: spacing.md,
      paddingRight: spacing.xs,
    },
    amountRowError: { borderColor: colors.danger },
    amountInput: {
      flex: 1,
      color: colors.text,
      fontSize: fontSize.md,
    },
    unitToggle: { flexDirection: 'row', gap: 4 },
    unitToggleRow: { flexDirection: 'row', gap: 4, alignSelf: 'flex-end' },
    shareRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginTop: spacing.xs },
    shareName: { flex: 1, color: colors.text, fontSize: fontSize.md, fontWeight: '600' },
    shareInputWrap: { flexDirection: 'row', alignItems: 'center', gap: 4 },
    shareInput: {
      width: 60, height: 44, borderRadius: radius.md, borderWidth: 1, borderColor: colors.border,
      backgroundColor: colors.surfaceAlt, color: colors.text, textAlign: 'center', fontSize: fontSize.md, fontWeight: '700',
    },
    sharePercentSign: { color: colors.textMuted, fontSize: fontSize.sm },
    currencyTrigger: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.xs,
      alignSelf: 'flex-start',
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radius.md,
      paddingVertical: 7,
      paddingHorizontal: spacing.sm,
      backgroundColor: colors.surfaceAlt,
    },
    currencyTriggerSymbol: {
      color: colors.text,
      fontSize: fontSize.md,
      fontWeight: '800',
      minWidth: 14,
      textAlign: 'center',
    },
    currencyTriggerLabel: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
    },
    currencyDropdown: {
      marginTop: 4,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radius.md,
      backgroundColor: colors.surface,
      overflow: 'hidden',
    },
    currencyOption: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
      paddingVertical: 10,
      paddingHorizontal: spacing.md,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: colors.border,
    },
    currencyOptionSelected: {
      backgroundColor: `${colors.primary}12`,
    },
    currencyOptionSymbol: {
      color: colors.textMuted,
      fontSize: fontSize.md,
      fontWeight: '700',
      width: 20,
      textAlign: 'center',
    },
    currencyOptionSymbolSelected: {
      color: colors.primary,
    },
    currencyOptionLabel: {
      color: colors.text,
      fontSize: fontSize.sm,
      flex: 1,
    },
    currencyOptionLabelSelected: {
      color: colors.primary,
      fontWeight: '700',
    },
    currencyOptionCode: {
      color: colors.textMuted,
      fontSize: fontSize.xs,
    },
    unitChip: {
      minWidth: 40,
      alignItems: 'center',
      borderRadius: radius.sm,
      borderWidth: 1,
      borderColor: colors.border,
      paddingVertical: 6,
      paddingHorizontal: spacing.sm,
    },
    unitChipSelected: {
      backgroundColor: colors.primary,
      borderColor: colors.primary,
    },
    unitChipText: {
      color: colors.textMuted,
      fontSize: fontSize.md,
      fontWeight: '700',
    },
    unitChipTextSelected: { color: colors.textOnPrimary },
    readonly: {
      height: 52,
      justifyContent: 'center',
      backgroundColor: colors.surfaceAlt,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radius.md,
      paddingHorizontal: spacing.md,
    },
    readonlyText: {
      color: colors.text,
      fontSize: fontSize.md,
      fontWeight: '600',
    },
    typeCards: {
      flexDirection: 'row',
      gap: spacing.xs,
    },
    typeCard: {
      flex: 1,
      alignItems: 'center',
      gap: 4,
      padding: spacing.sm,
      borderRadius: radius.md,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
    },
    typeCardSelected: {
      borderColor: colors.primary,
      backgroundColor: `${colors.primary}14`,
    },
    typeCardLabel: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      fontWeight: '700',
    },
    typeCardLabelSelected: {
      color: colors.primary,
    },
    typeCardSub: {
      color: colors.textMuted,
      fontSize: 10,
      textAlign: 'center',
    },
    labelChips: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: spacing.xs,
      marginTop: spacing.xs,
    },
    labelChip: {
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radius.full,
      paddingVertical: 5,
      paddingHorizontal: spacing.sm,
    },
    labelChipSelected: {
      backgroundColor: colors.primary,
      borderColor: colors.primary,
    },
    labelChipText: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      fontWeight: '600',
    },
    labelChipTextSelected: { color: colors.textOnPrimary },
    suggestionList: {
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radius.md,
      overflow: 'hidden',
    },
    suggestion: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
      backgroundColor: colors.surface,
      borderWidth: 1,
      borderColor: colors.primary,
      borderRadius: radius.md,
      paddingVertical: spacing.sm,
      paddingHorizontal: spacing.md,
    },
    suggestionInfo: { flex: 1 },
    suggestionName: {
      color: colors.text,
      fontSize: fontSize.sm,
      fontWeight: '600',
    },
    suggestionSub: {
      color: colors.textMuted,
      fontSize: fontSize.xs ?? 11,
    },
    suggestionSelected: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      height: 52,
      backgroundColor: colors.surfaceAlt,
      borderWidth: 1,
      borderColor: colors.primary,
      borderRadius: radius.md,
      paddingHorizontal: spacing.md,
    },
    suggestionSelectedInfo: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
    },
    multiline: {
      // Σταθερό (όχι minHeight) ώστε το TextInput να κάνει το δικό του εσωτερικό
      // scroll όταν το κείμενο ξεπερνάει το ύψος — αλλιώς σε Android το κείμενο
      // "ξεχείλιζε" οπτικά έξω από το πλαίσιο και καλυπτόταν από το κουμπί
      // "Επισκόπηση" που ακολουθούσε από κάτω.
      height: 180,
      paddingTop: spacing.md,
      textAlignVertical: 'top',
    },
    formError: {
      color: colors.danger,
      fontSize: fontSize.sm,
    },
    cooldownBox: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: spacing.sm,
      backgroundColor: 'rgba(251,191,36,0.08)',
      borderWidth: 1,
      borderColor: 'rgba(251,191,36,0.30)',
      borderRadius: radius.md,
      padding: spacing.md,
    },
    cooldownInfo: { flex: 1, gap: spacing.sm },
    cooldownTitle: {
      color: '#FBBF24',
      fontSize: fontSize.sm,
      fontWeight: '700',
    },
    cooldownText: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      lineHeight: 20,
    },
    cooldownTime: {
      color: colors.text,
      fontWeight: '700',
    },
    cooldownCancel: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      textAlign: 'center',
    },
    // Review step
    reviewCard: {
      backgroundColor: colors.surface,
      borderRadius: radius.lg,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.lg,
      gap: spacing.md,
    },
    reviewTitle: {
      color: colors.text,
      fontSize: fontSize.md,
      fontWeight: '700',
      marginBottom: spacing.xs,
    },
    reviewRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: spacing.sm,
    },
    reviewRowFull: {
      gap: spacing.xs,
    },
    reviewLabel: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      fontWeight: '500',
      minWidth: 100,
    },
    reviewValue: {
      color: colors.text,
      fontSize: fontSize.sm,
      fontWeight: '600',
      flex: 1,
      textAlign: 'right',
    },
    reviewValueMuted: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      lineHeight: 20,
    },
    // Tooltip
    tooltipOverlay: {
      flex: 1,
      backgroundColor: 'rgba(0,0,0,0.5)',
      justifyContent: 'center',
      alignItems: 'center',
      padding: spacing.xl,
    },
    tooltipCard: {
      backgroundColor: colors.surface,
      borderRadius: radius.lg,
      padding: spacing.lg,
      gap: spacing.md,
      width: '100%',
    },
    tooltipHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
    },
    tooltipTitle: {
      color: colors.text,
      fontSize: fontSize.md,
      fontWeight: '700',
      flex: 1,
    },
    tooltipText: {
      color: colors.textMuted,
      fontSize: fontSize.md,
      lineHeight: 22,
    },
    tooltipClose: {
      alignSelf: 'flex-end',
      backgroundColor: colors.primary,
      borderRadius: radius.full,
      paddingVertical: spacing.sm,
      paddingHorizontal: spacing.lg,
    },
    tooltipCloseText: {
      color: colors.textOnPrimary,
      fontSize: fontSize.sm,
      fontWeight: '700',
    },
    // Coach bottom sheet
    coachOverlay: {
      flex: 1,
    },
    coachSheet: {
      backgroundColor: colors.surface,
      borderTopLeftRadius: radius.xl,
      borderTopRightRadius: radius.xl,
      padding: spacing.lg,
      gap: spacing.md,
      shadowColor: '#000',
      shadowOpacity: 0.2,
      shadowRadius: 16,
      shadowOffset: { width: 0, height: -4 },
      elevation: 12,
    },
    coachHandle: {
      width: 40,
      height: 4,
      borderRadius: 2,
      backgroundColor: colors.border,
      alignSelf: 'center',
      marginBottom: spacing.xs,
    },
    coachDots: {
      flexDirection: 'row',
      justifyContent: 'center',
      gap: 6,
    },
    coachDot: {
      width: 6,
      height: 6,
      borderRadius: 3,
      backgroundColor: colors.border,
    },
    coachDotActive: {
      backgroundColor: colors.primary,
      width: 18,
    },
    coachIconWrap: {
      alignItems: 'center',
      paddingTop: spacing.xs,
    },
    coachTitle: {
      color: colors.text,
      fontSize: fontSize.lg,
      fontWeight: '700',
      textAlign: 'center',
    },
    coachText: {
      color: colors.textMuted,
      fontSize: fontSize.md,
      lineHeight: 22,
      textAlign: 'center',
    },
    coachActions: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: spacing.xs,
    },
    coachSkip: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
    },
    coachNext: {
      backgroundColor: colors.primary,
      borderRadius: radius.full,
      paddingVertical: spacing.sm,
      paddingHorizontal: spacing.lg,
    },
    coachNextText: {
      color: colors.textOnPrimary,
      fontSize: fontSize.sm,
      fontWeight: '700',
    },
  });
}
